/**
 * Pure check that a LinkedIn Ads connection was granted campaign-management
 * access (`rw_ads`) before Mora tries to create anything on it.
 *
 * Stored scopes are a claim with an age, so an unknown answer (no scopes
 * recorded anywhere) is allowed through and the provider remains the judge.
 * Only a recorded, non-empty scope list that lacks `rw_ads` is refused, before
 * any write, with a message that names the fix.
 */

export const LINKEDIN_WRITE_SCOPE = 'rw_ads';
export const LINKEDIN_WRITE_SCOPE_MISSING_CODE = 'linkedin_write_scope_missing';
export const LINKEDIN_WRITE_SCOPE_MISSING_MESSAGE =
  'Reconnect LinkedIn Ads with campaign-management access.';

export function parseLinkedInScopes(value: unknown): string[] {
  if (Array.isArray(value))
    return value.filter((scope): scope is string => typeof scope === 'string' && scope.length > 0);
  if (typeof value === 'string')
    return value
      .split(/[\s,]+/)
      .map((scope) => scope.trim())
      .filter(Boolean);
  return [];
}

/** `grantedScopes` (the column) wins over `connectionDataScopes` (the legacy JSON copy) when it lists anything. */
export function linkedInWriteScopeVerdict(input: {
  grantedScopes?: unknown;
  connectionDataScopes?: unknown;
}): { ok: true } | { ok: false; code: string; message: string } {
  const granted = parseLinkedInScopes(input.grantedScopes);
  const scopes = granted.length > 0 ? granted : parseLinkedInScopes(input.connectionDataScopes);
  if (scopes.length === 0 || scopes.includes(LINKEDIN_WRITE_SCOPE)) return { ok: true };
  return {
    ok: false,
    code: LINKEDIN_WRITE_SCOPE_MISSING_CODE,
    message: LINKEDIN_WRITE_SCOPE_MISSING_MESSAGE,
  };
}
